import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Truck, Package, RefreshCw, Loader2, CheckCircle2, FileText, DollarSign, FileMinus, X, Map,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import ShipmentMap from '../../components/ShipmentMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_FULFILMENT = `${BACKEND_URL}/api/gov/fulfilment`;
const API_FINANCE = `${BACKEND_URL}/api/finance`;

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (v) => (v ? new Date(v).toLocaleString() : '—');

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((r) => r?.msg || JSON.stringify(r)).join('; ');
  if (typeof d === 'string') return d;
  return e?.message || '';
};

const STATUS_STYLE = {
  approved:        'bg-emerald-100 text-emerald-800',
  uske_processing: 'bg-blue-100 text-blue-800',
  in_transit:      'bg-amber-100 text-amber-800',
  delivered:       'bg-emerald-100 text-emerald-800',
  finalized:       'bg-slate-200 text-slate-700',
};

const GovFulfilment = () => {
  const { user } = useAuth();
  const isFrontDesk = user?.role === 'uske_front_desk';
  const isUskeFO    = user?.role === 'uske_finance_officer';
  const isUske      = user?.role === 'super_admin' || user?.role === 'admin';
  const isViewOnly  = !!user?.view_only;

  const [tab, setTab] = useState(isUskeFO ? 'delivered' : 'approved');
  const [showMap, setShowMap] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState(null); // { kind, order }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_FULFILMENT}/orders`, { params: { status: tab } });
      setOrders(r.data || []);
    } catch (e) {
      toast({ title: 'Load failed', description: errMsg(e), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const startProcessing = async (o) => {
    try {
      await axios.post(`${API_FULFILMENT}/orders/${o.order_id}/start-processing`);
      toast({ title: 'Started processing', description: `${o.order_id} is now USKE Processing.` });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: errMsg(e), variant: 'destructive' });
    }
  };

  const tabs = [];
  if (isFrontDesk || isUske) {
    tabs.push({ id: 'approved',        label: 'Approved',      icon: CheckCircle2 });
    tabs.push({ id: 'uske_processing', label: 'Processing',    icon: Package });
    tabs.push({ id: 'in_transit',      label: 'In Transit',    icon: Truck });
  }
  if (isUskeFO || isUske) {
    tabs.push({ id: 'delivered',       label: 'Awaiting Finalization', icon: CheckCircle2 });
    tabs.push({ id: 'finalized',       label: 'Finalized (Invoiced)', icon: FileText });
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4" data-testid="uske-fulfilment-page">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg"><Truck className="h-6 w-6 text-blue-700" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">USKE Fulfilment</h1>
          <p className="text-sm text-slate-500">
            {isFrontDesk ? 'Ship approved orders and hand off to the delivery driver.'
              : isUskeFO ? 'Review delivered orders, finalize, record ACH payments, and issue credit notes.'
              : 'USKE fulfilment queue.'}
          </p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
                    data-testid={`ff-tab-${t.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm ${active ? 'bg-blue-700 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
        <div className="flex-1" />
        {(tab === 'delivered' || tab === 'finalized') && (
          <Button size="sm" variant={showMap ? 'default' : 'outline'} onClick={() => setShowMap((v) => !v)}
                  className={showMap ? 'bg-blue-700 hover:bg-blue-800 text-white' : ''}
                  data-testid="ff-toggle-map">
            <Map className="h-4 w-4 mr-1" /> {showMap ? 'Hide map' : 'Show map'}
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="ff-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {showMap && (tab === 'delivered' || tab === 'finalized') && (
        <Card className="border-2" data-testid="ff-map-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Map className="h-4 w-4 text-slate-500" /> Delivery Map (POD Geotags)</CardTitle></CardHeader>
          <CardContent>
            <ShipmentMap
              height={360}
              points={(orders || []).map((o) => o.delivery_geo && ({
                id: o.order_id,
                lat: o.delivery_geo.lat, lng: o.delivery_geo.lng,
                title: o.order_id,
                subtitle: `${o.agency_name || '—'} · ${money(o.total)} · ${o.delivery_recipient_name || 'Delivered'}`,
              })).filter(Boolean)}
            />
          </CardContent>
        </Card>
      )}

      {loading && orders.length === 0 ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>
      ) : orders.length === 0 ? (
        <div className="text-slate-400 text-sm py-8 text-center">Nothing in this queue.</div>
      ) : (
        <div className="grid gap-3">
          {orders.map((o) => (
            <Card key={o.order_id} className="border-2" data-testid={`ff-card-${o.order_id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800">{o.order_id}</span>
                      <span className={`text-[11px] px-1.5 py-0.5 rounded ${STATUS_STYLE[o.status] || 'bg-slate-100 text-slate-600'}`}>
                        {o.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{o.agency_name} · {o.purchaser_name || o.purchaser_email}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {o.delivery_location?.name ? `${o.delivery_location.name} · ` : ''}
                      {o.delivery_location?.address_line1 || (typeof o.shipping_address === 'string' ? o.shipping_address : [o.shipping_address?.address, o.shipping_address?.city].filter(Boolean).join(', ')) || '—'}
                    </div>
                    {o.tracking_number && <div className="text-[11px] text-slate-500 mt-0.5">Tracking: {o.tracking_number} ({o.carrier || 'carrier n/a'})</div>}
                    {o.invoice_no && <div className="text-[11px] text-emerald-700 mt-0.5">Invoice: {o.invoice_no}</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-slate-800">{money(o.total)}</div>
                    <div className="text-[11px] text-slate-500">{o.item_count} item{o.item_count === 1 ? '' : 's'}</div>
                    <div className="text-[11px] text-slate-400">Updated {fmtDate(o.updated_at)}</div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {isViewOnly && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border" data-testid={`ff-viewonly-${o.order_id}`}>
                      View only
                    </span>
                  )}
                  {tab === 'approved' && (isFrontDesk || isUske) && !isViewOnly && (
                    <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => startProcessing(o)} data-testid={`ff-start-processing-${o.order_id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Start Processing
                    </Button>
                  )}
                  {tab === 'uske_processing' && (isFrontDesk || isUske) && !isViewOnly && (
                    <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white" onClick={() => setDlg({ kind: 'ship', order: o })} data-testid={`ff-ship-${o.order_id}`}>
                      <Truck className="h-4 w-4 mr-1" /> Mark In Transit
                    </Button>
                  )}
                  {tab === 'delivered' && (isUskeFO || isUske) && !isViewOnly && (
                    <Button size="sm" className="bg-green-700 hover:bg-green-800 text-white" onClick={() => setDlg({ kind: 'finalize', order: o })} data-testid={`ff-finalize-${o.order_id}`}>
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Finalize (Invoice)
                    </Button>
                  )}
                  {tab === 'finalized' && (isUskeFO || isUske) && !isViewOnly && o.invoice_no && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => setDlg({ kind: 'payment', order: o })} data-testid={`ff-record-payment-${o.order_id}`}>
                        <DollarSign className="h-4 w-4 mr-1" /> Record Payment
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDlg({ kind: 'credit_note', order: o })} data-testid={`ff-credit-note-${o.order_id}`}>
                        <FileMinus className="h-4 w-4 mr-1" /> Issue Credit Note
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {dlg?.kind === 'ship' && <ShipDialog order={dlg.order} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />}
      {dlg?.kind === 'finalize' && <FinalizeDialog order={dlg.order} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />}
      {dlg?.kind === 'payment' && <PaymentDialog order={dlg.order} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />}
      {dlg?.kind === 'credit_note' && <CreditNoteDialog order={dlg.order} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />}
    </div>
  );
};

// ------- Dialogs -----------------------------------------------------------

const DialogFrame = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3">
    <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
      <div className="px-4 py-3 border-b flex items-center gap-2">
        <div className="font-semibold flex-1">{title}</div>
        <Button size="icon" variant="ghost" onClick={onClose}><X className="h-4 w-4" /></Button>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  </div>
);

const ShipDialog = ({ order, onClose, onDone }) => {
  const [carrier, setCarrier] = useState('');
  const [tracking, setTracking] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_FULFILMENT}/orders/${order.order_id}/mark-in-transit`, {
        carrier: carrier || null, tracking_number: tracking || null, notes: notes || null,
      });
      toast({ title: 'Marked in transit' });
      onDone();
    } catch (e) { toast({ title: 'Failed', description: errMsg(e), variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  return (
    <DialogFrame title={`Ship ${order.order_id}`} onClose={onClose}>
      <div><Label className="text-xs">Carrier</Label><Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="UPS / USPS / FedEx" className="mt-1" data-testid="ship-carrier" /></div>
      <div><Label className="text-xs">Tracking #</Label><Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="1Z999..." className="mt-1" data-testid="ship-tracking" /></div>
      <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="ship-notes" /></div>
      <div className="flex gap-2 justify-end pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="ship-confirm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Truck className="h-4 w-4 mr-1" />}Confirm
        </Button>
      </div>
    </DialogFrame>
  );
};

const FinalizeDialog = ({ order, onClose, onDone }) => {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API_FULFILMENT}/orders/${order.order_id}/finalize`, { notes: notes || null });
      toast({ title: 'Finalized', description: `Invoice ${r.data.invoice_no} issued.` });
      onDone();
    } catch (e) { toast({ title: 'Failed', description: errMsg(e), variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  return (
    <DialogFrame title={`Finalize ${order.order_id}`} onClose={onClose}>
      <div className="text-sm text-slate-600">
        This will <b>issue a Net-30 invoice</b> with USKE ACH instructions and email the purchaser.
      </div>
      <div><Label className="text-xs">Notes (optional)</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="finalize-notes" /></div>
      <div className="flex gap-2 justify-end pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving} className="bg-green-700 hover:bg-green-800 text-white" data-testid="finalize-confirm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}Finalize & Invoice
        </Button>
      </div>
    </DialogFrame>
  );
};

const PaymentDialog = ({ order, onClose, onDone }) => {
  const [amount, setAmount] = useState(order.total || '');
  const [method, setMethod] = useState('ach');
  const [ref, setRef] = useState('');
  const [bank, setBank] = useState('');
  const [notes, setNotes] = useState('');
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [prefill, setPrefill] = useState(null);

  useEffect(() => {
    if (!order.invoice_id) return;
    (async () => {
      try {
        const r = await axios.get(`${API_FINANCE}/invoices/${order.invoice_id}/latest-notification`);
        if (r.data) {
          const n = r.data;
          if (n.amount != null) setAmount(String(n.amount));
          if (n.method) setMethod(n.method);
          if (n.reference_number) setRef(n.reference_number);
          if (n.bank) setBank(n.bank);
          if (n.notes) setNotes(n.notes);
          if (n.payment_date) setPayDate(new Date(n.payment_date).toISOString().slice(0, 10));
          setPrefill({ by: n.notified_by_name || n.notified_by_email, at: n.notified_at });
        }
      } catch (_) { /* best-effort */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.invoice_id]);

  const save = async () => {
    if (!order.invoice_id) { toast({ title: 'No invoice on this order', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      await axios.post(`${API_FINANCE}/invoices/${order.invoice_id}/payments`, {
        amount: parseFloat(amount), method, reference_number: ref || null,
        bank: bank || null, notes: notes || null,
        payment_date: new Date(payDate).toISOString(),
      });
      toast({ title: 'Payment recorded' });
      onDone();
    } catch (e) { toast({ title: 'Failed', description: errMsg(e), variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  return (
    <DialogFrame title={`Record Payment · ${order.invoice_no}`} onClose={onClose}>
      {prefill && (
        <div className="text-[11px] bg-blue-50 border border-blue-200 rounded p-2 text-blue-900" data-testid="ff-pay-prefill-banner">
          Prefilled from Agency notification by <b>{prefill.by || '—'}</b>
          {prefill.at ? ` · ${new Date(prefill.at).toLocaleString()}` : ''}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Amount *</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="pay-amount" /></div>
        <div>
          <Label className="text-xs">Method</Label>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full mt-1 border rounded px-2 py-1.5 text-sm bg-white" data-testid="pay-method">
            <option value="ach">ACH</option><option value="eft">EFT</option><option value="wire">Wire</option>
          </select>
        </div>
      </div>
      <div><Label className="text-xs">Payment date</Label><Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="mt-1" data-testid="pay-date" /></div>
      <div><Label className="text-xs">Reference #</Label><Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="ACH-####" className="mt-1" data-testid="pay-ref" /></div>
      <div><Label className="text-xs">Bank</Label><Input value={bank} onChange={(e) => setBank(e.target.value)} className="mt-1" data-testid="pay-bank" /></div>
      <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="pay-notes" /></div>
      <div className="flex gap-2 justify-end pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="pay-confirm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <DollarSign className="h-4 w-4 mr-1" />}Record
        </Button>
      </div>
    </DialogFrame>
  );
};

const CreditNoteDialog = ({ order, onClose, onDone }) => {
  const [total, setTotal] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!order.invoice_id) { toast({ title: 'No invoice on this order', variant: 'destructive' }); return; }
    if (!reason.trim()) { toast({ title: 'Reason required', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      await axios.post(`${API_FINANCE}/credit-notes`, {
        invoice_id: order.invoice_id,
        total: parseFloat(total),
        subtotal: parseFloat(total),
        reason: reason.trim(),
      });
      toast({ title: 'Credit note issued' });
      onDone();
    } catch (e) { toast({ title: 'Failed', description: errMsg(e), variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  return (
    <DialogFrame title={`Issue Credit Note · ${order.invoice_no}`} onClose={onClose}>
      <div><Label className="text-xs">Amount *</Label><Input type="number" step="0.01" value={total} onChange={(e) => setTotal(e.target.value)} className="mt-1" data-testid="cn-amount" /></div>
      <div><Label className="text-xs">Reason *</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Damaged goods, partial return, etc." className="mt-1" data-testid="cn-reason" /></div>
      <div className="flex gap-2 justify-end pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving} className="bg-orange-700 hover:bg-orange-800 text-white" data-testid="cn-confirm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileMinus className="h-4 w-4 mr-1" />}Issue
        </Button>
      </div>
    </DialogFrame>
  );
};

export default GovFulfilment;
